import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "deliveries" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "switch-label" }, "Toon voltooide opleveringen:", -1)
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ls } from '@/ls'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import axios from 'axios'
import { deliveryStore } from '@/stores/delivery'
import PageTitleBar from '@/components/PageTitleBar.vue'
import { DeliveryStateLabels, DeliveryStateLabelColors } from '@/helpers/deliveryStates'
import ColumnSettings from '@/components/ColumnSettings.vue'
import FilterBox from '@/components/FilterBox.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'Deliveries',
  setup(__props) {

const router = useRouter()

const tableSettings = ref<TableSettings>(ls.get('DeliveriesTableSettings') || {
  pagination: { current: 1, pageSize: 25 },
  showSizeChanger: true,
  pageSizeOptions: ['25', '50', '100'],
  activeFilters: null,
  sort: null,
  search: null,
  archived: false
})

const columns = ref([
  {
    title: '#',
    key: 'id',
    sorter: true
  },
  {
    title: 'Type',
    key: 'type',
    filters: [
      {
        text: 'Voorschouw',
        value: 'inspection'
      },
      {
        text: 'Oplevering',
        value: 'delivery'
      }
    ],
    sorter: false
  },
  {
    title: 'Project',
    key: 'project.id',
    filters: [],
    sorter: false
  },
  {
    title: 'Bouwnummer',
    key: 'building.number',
    sorter: true
  },
  {
    title: 'Adres',
    key: 'building.address',
    sorter: false
  },
  {
    title: 'Uitvoeren op',
    key: 'scheduled_at',
    sorter: true
  },
  {
    title: 'Toegewezen',
    key: 'scheduled_user',
    sorter: false
  },
  {
    title: 'Status',
    key: 'status'
  }
])

const hiddenColumns = ref<string[]>([])
const filteredColumns = computed(() => {
  return columns.value.filter(c => !hiddenColumns.value.includes(c.key))
})

onMounted(() => {
  deliveryStore().getDeliveries(tableSettings.value)
  getFilters()
  onTableSettingsChange()
})

function getFilters () : void {
  getProjectFilter()
}

function getProjectFilter () : void {
  axios.post(process.env.VUE_APP_API_URL + '/filters/project/deliveries', { textColumn: 'name', textColumn2: 'reference', valueColumn: 'id' }).then((r) => {
    columns.value[2].filters = r.data
  })
}

function onCustomRowClick (record:{ id:number, type:string }) : { onClick: () => void } {
  return {
    onClick: () => {
      if (record.type === 'inspection') {
        router.push({ name: 'InspectionShow', params: { deliveryId: record.id.toString() } })
      } else {
        router.push({ name: 'DeliveryShow', params: { deliveryId: record.id.toString() } })
      }
    }
  }
}

watch(() => tableSettings, () => {
  onTableSettingsChange()
}, { deep: true })

function handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
  // Remove key from filters if filter is an empty object.
  if (filters && Object.keys(filters).length) {
    Object.keys(filters).forEach((key) => {
      if (!filters[key] || !filters[key].length) {
        delete filters[key]

        columns.value.forEach((column:Column) => {
          if (column.key === key) {
            column.filteredValue = null
          }
        })
      }
    })
  }

  // Set pagination, filters and sorting on tableSettings.
  tableSettings.value.pagination = pagination
  tableSettings.value.activeFilters = filters
  tableSettings.value.sort = sort.order ? sort : null

  // Get list based on filters and search term.
  deliveryStore().getDeliveries(tableSettings.value)
}

function handleSearchChange (searchTerm:string) : void {
  tableSettings.value.search = searchTerm

  // Set page to 1 because of new search but retain filters and sorting.
  tableSettings.value.pagination.current = 1

  // Get list based on filters and search term.
  deliveryStore().getDeliveries(tableSettings.value)
}

function onTableSettingsChange () : void {
  // On tableSettings change, store them in local storage.
  ls.set('DeliveriesTableSettings', tableSettings.value)

  axios.post(process.env.VUE_APP_API_URL + '/users/constructionSiteApp/settings', {
    settingName: 'DeliveriesTableSettings',
    settingValue: tableSettings.value
  })

  // Set the current active filters on the columns.
  columns.value.forEach((column:Column) => {
    // Set active filters.
    if (tableSettings.value.activeFilters && column.key in tableSettings.value.activeFilters) {
      column.filteredValue = tableSettings.value.activeFilters[column.key]
    } else {
      column.filteredValue = null
    }

    // Set active sort order.
    if (tableSettings.value.sort && column.key === tableSettings.value.sort.columnKey) {
      column.defaultSortOrder = tableSettings.value.sort.order
      column.sortOrder = tableSettings.value.sort.order
    } else {
      column.sortOrder = false
    }
  })
}

function updateFilters (activeFilters:{[key: string]: number[]|string[]}) : void {
  tableSettings.value.activeFilters = activeFilters
  deliveryStore().getDeliveries(tableSettings.value)
}

const pagination = computed(() => {
  return {
    current: tableSettings.value.pagination.current,
    defaultPageSize: tableSettings.value.pagination.pageSize,
    pageSize: parseInt(deliveryStore().deliveries.meta.per_page),
    total: deliveryStore().deliveries.meta.total,
    showTotal: (total:number, range:number[]) => `Voorschouw / Oplevering ${range[0]} t/m ${range[1]} van de ${total}`,
    showSizeChanger: tableSettings.value.showSizeChanger,
    pageSizeOptions: tableSettings.value.pageSizeOptions
  }
})

return (_ctx: any,_cache: any) => {
  const _component_a_switch = _resolveComponent("a-switch")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_table = _resolveComponent("a-table")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PageTitleBar, null, {
      title: _withCtx(() => [
        _createTextVNode("Voorschouwen en opleveren.")
      ]),
      buttons: _withCtx(() => [
        _createVNode(ColumnSettings, {
          columns: columns.value,
          storageName: "hiddenDeliveryColumns",
          onHiddenColumnsChanged: _cache[0] || (_cache[0] = ($event: any) => (hiddenColumns.value = $event))
        }, null, 8, ["columns"]),
        _createVNode(_component_a_switch, {
          checked: tableSettings.value.archived,
          "onUpdate:checked": _cache[1] || (_cache[1] = ($event: any) => ((tableSettings.value.archived) = $event)),
          onChange: _cache[2] || (_cache[2] = ($event: any) => (_unref(deliveryStore)().getDeliveries(tableSettings.value)))
        }, null, 8, ["checked"]),
        _createTextVNode(),
        _hoisted_2
      ]),
      search: _withCtx(() => [
        _createVNode(_component_a_input_search, {
          placeholder: "Zoeken..",
          style: {"width":"100%"},
          value: tableSettings.value.search,
          "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((tableSettings.value.search) = $event)),
          onSearch: handleSearchChange,
          "enter-button": "",
          "allow-clear": ""
        }, null, 8, ["value"])
      ]),
      _: 1
    }),
    _createVNode(FilterBox, {
      view: "deliveries",
      "active-filters": tableSettings.value.activeFilters,
      columns: columns.value,
      onUpdate: updateFilters
    }, null, 8, ["active-filters", "columns"]),
    _createVNode(_component_a_table, {
      columns: _unref(filteredColumns),
      "data-source": _unref(deliveryStore)().deliveries.data,
      loading: _unref(deliveryStore)().loading.list,
      rowKey: "id",
      customRow: onCustomRowClick,
      scroll: { x: true },
      pagination: _unref(pagination),
      onChange: handleTableChange
    }, {
      bodyCell: _withCtx(({ column, record }) => [
        (column.key === 'id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(_toDisplayString(record.id), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'type')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode(_toDisplayString(record.type_label), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'project.id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createTextVNode(_toDisplayString(record.project ? record.project.name + ' (' + record.project.reference + ')' : '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'building.number')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createTextVNode(_toDisplayString(record.building ? record.building.number : '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'building.address')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
              (record.building)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(record.building.address), 1))
                : (_openBlock(), _createElementBlock("span", _hoisted_4, "-"))
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'scheduled_at')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
              _createTextVNode(_toDisplayString(record.scheduled_at ? new Date(record.scheduled_at).toLocaleDateString('nl-NL') : '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'scheduled_user')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
              _createTextVNode(_toDisplayString(record.scheduled_user ? record.scheduled_user.full_name : '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'status')
          ? (_openBlock(), _createBlock(_component_a_tag, {
              key: 7,
              color: _unref(DeliveryStateLabelColors)[record.status]
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(record.status ? _unref(DeliveryStateLabels)[record.status] : '-'), 1)
              ]),
              _: 2
            }, 1032, ["color"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["columns", "data-source", "loading", "pagination"])
  ]))
}
}

})